import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
// eslint-disable-next-line import/namespace, import/named
import { graphql } from 'gatsby';
import { PAGE_TITLE } from '../modules/constants';
import { organiseMenu } from '../modules/filtering';

const PAGE_MARKET = process.env.GATSBY_MARKET;
let runOnce = false;

import {
    SplitBanner,
    BottomButton,
    CookieBanner,
    GoogleAnalytics,
    Filter,
    Footer,
    PDP,
    Navigation,
    Section,
    Shade,
    SiteNav,
    SEO,
    TopBanner
} from '../components';

import { NUTRITION_ADULT_REQUIREMENT } from '../modules/constants';

let sentryInit = false;

/**
 * This is the page that renders the menu. It takes data from the GraphQL query at the bottom
 * of the page at build time and makes it available to the application as `data`
 * State is kept in the application via React useState.
 * We set up the various props required for the application using this data and feed it to the various components.
 *
 * @function
 * @param       {object} data The data from the API.
 * @constructor
 */
function IndexPage({ data }) {
    if (
        typeof process.env.GATSBY_SENTRY_DSN !== 'undefined' &&
        sentryInit === false
    ) {
        Sentry.init({
            environment: process.env.GATSBY_SENTRY_ENVIRONMENT,
            dsn: process.env.GATSBY_SENTRY_DSN,
            integrations: [new Integrations.BrowserTracing()],
            tracesSampleRate: 0.1
        });
        sentryInit = true;
    }
    const { sections: rawSections, bastes } = data.nandos.menu;
    const [allergenFilter, setAllergenFilter] = useState({ HIDETRACES: false });
    const [dietFilter, setDietFilter] = useState([]);
    const [hideCalories, setHideCalories] = useState(false);
    const [showFilter, setShowFilter] = useState(null);
    const [pdpInfo, setPdpInfo] = useState(null);
    const [filterCount, setFilterCount] = useState(0);
    const [shade, setShade] = useState({});

    const sections = organiseMenu(rawSections);

    const shadeProps = {
        shade,
        setShade
    };

    const filterProps = {
        allergenFilter,
        dietFilter,
        filterCount,
        hideCalories,
        setHideCalories,
        setAllergenFilter,
        setDietFilter,
        setFilterCount,
        setShowFilter,
        showFilter
    };

    const navProps = {
        filterCount,
        sections,
        showFilter,
        setFilterCount,
        setShowFilter,
        setShade,
        allergenFilter,
        setAllergenFilter
    };

    const pdpProps = {
        pdpInfo,
        setPdpInfo,
        setShade,
        hideCalories
    };

    const props = {
        title: PAGE_TITLE,
        allergenFilter,
        dietFilter
    };

    const splitBannerSelectedSection = sections.find(
        (section) => section.kind === 'BURGERS_PITTAS_WRAPS'
    );

    useEffect(() => {
        if (typeof document !== 'undefined' && !runOnce) {
            const alterText =
                /.*Windows.*/.test(navigator.userAgent) &&
                !/.*Edg.*/.test(navigator.userAgent);

            if (alterText) {
                document.documentElement.classList.add('alterbaselinetext');
            }
            runOnce = true;
        }
    }, []);

    return (
        <>
            <GoogleAnalytics market={PAGE_MARKET} />
            <SiteNav style='main' market={PAGE_MARKET} {...shadeProps} />
            <main>
                <TopBanner market={PAGE_MARKET} />
                <SEO {...props} market={PAGE_MARKET} />
                <Navigation {...navProps} />
                <Filter {...filterProps} />

                {splitBannerSelectedSection && (
                    <SplitBanner
                        altText={'PERi-Mac Wrap - Highway to the comfort zone'}
                        section={splitBannerSelectedSection}
                        market={PAGE_MARKET}
                        isClickable={true}
                    />
                )}
                {sections &&
                    sections.length &&
                    sections.map((section, idx) => {
                        const sectionProps = {
                            ...section,
                            allergenFilter,
                            dietFilter,
                            pdpInfo,
                            hideCalories,
                            idx,
                            setPdpInfo,
                            bastes,
                            nandinos: section.kind === 'NANDINOS'
                        };
                        return (
                            <Section
                                {...sectionProps}
                                key={section.id}
                                className={`menuSection colour${idx}`}
                            />
                        );
                    })}
                <div className='nutritionalmain'>
                    <p>{NUTRITION_ADULT_REQUIREMENT}</p>
                </div>
            </main>
            <PDP {...pdpProps} market={PAGE_MARKET} />
            <BottomButton market={PAGE_MARKET} />
            <Shade {...shadeProps} />
            <Footer market={PAGE_MARKET} />
            <CookieBanner />
        </>
    );
}

IndexPage.propTypes = {
    data: PropTypes.object.isRequired
};

export default IndexPage;

export const query = graphql`
    query NandosAPI($market: NANDOS_Market, $restaurantSlug: String) {
        nandos {
            menu(market: $market, restaurantSlug: $restaurantSlug) {
                bastes {
                    description
                    displayName
                    slug
                    restaurantGroup
                    nutritionalInfo {
                        energyKcal
                    }
                    colour {
                        hexCode
                    }
                    flags
                }
                sections {
                    id
                    displayName
                    kind
                    items(fulfilmentType: EAT_IN) {
                        id
                        slug
                        servingInfo
                        plu
                        displayName
                        description
                        startDate
                        endDate
                        subsection
                        secondarySubsection
                        lozenge { 
                            label 
                            textColour 
                            backgroundColour
                        }
                        modifiers {
                            slug
                            selectableQuantity
                            promptLabel
                            showIf
                            options {
                                displayName
                                fulfilmentTypes
                                restaurantGroup
                                plu
                                price {
                                    currencyCode
                                    value
                                }
                                slug
                            }
                        }
                        allergens {
                            name
                            present
                            message
                        }
                        diets
                        flags
                        restaurantGroup
                        nutritionalInfo {
                            factsForPortionSizes {
                                alcoholByVolumePercent
                                energyKcal
                                energyKj
                                fatMg
                                fibreMg
                                proteinMg
                                saltMg
                                saturatesMg
                                sugarsMg
                                totalCarbsMg
                            }
                            messages
                        }
                        fulfilmentTypes
                        price {
                            currencyCode
                            value
                        }
                        priceList {
                            displayName
                            kind
                            price {
                                currencyCode
                                value
                            }
                        }
                        depositFee {
                            currencyCode
                            value
                        }
                        ageRestricted
                        image {
                            featured
                            aspectRatio_16_9 {
                                res640x360
                            }
                            altText
                        }
                    }
                }
            }
        }
    }
`;
